import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import ScaleIcon from '@src/svgs/ScaleIcon';
import UpDownIcon from '@src/svgs/UpDownIcon';
import { AspectRatio } from '@src/types.ts/video';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import React, { Fragment } from 'react';
import styled from 'styled-components';

import { FIT_DEFAULT } from '../../config/videoSettings';
import FadeProducer from '../../fadeEffectProcessor/FadeProducer';
import { numericalInputStyles } from '../../styles/mainStyle';
import SliderSelect from '../common/PlainSlider';
import { ElementAnimationSettings } from './ElementAnimationSettings';
import { GenericAnimationSettings } from './GenericAnimationSettings';
import { PropertyCaption } from './PropertyCaption';
import { PropertyNumericalInput } from './PropertyNumericalInput';
import useLayoutAwareVideoSettingsState from './useLayoutAwareVideoSettingsState';
import { VideoFitPropertySelect } from './VideoFitPropertySelect';
import { VideoPreset } from './VideoPreset';

import type { ElementState } from '../../renderer/ElementState';

interface VideoSettingsProps {
  activeElement: ElementState;
}

export const VideoSettings: React.FC<VideoSettingsProps> = props => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { enableStackedLayouts } = useFlagsCombination(
    userIdentity.currentRole,
  );

  const {
    activeTimelineVideoElement, // layout child's hidden parent or regular active
    childVideoElement, // layout child
    displayElement, // layout child or regular active
    isLayoutComposition,
    sourceUrl,
    xValue,
    yValue,
    modifyPropertyWithSlider,
    handleChangeScale,
    updateTimelineVideoProperty,
  } = useLayoutAwareVideoSettingsState({ activeElement: props.activeElement });

  if (!props.activeElement) {
    return null;
  }

  const isBroll = !videoCreator.isOriginalVideoElement(
    activeTimelineVideoElement.source,
  );
  const fadeProducer = new FadeProducer(
    videoCreator,
    activeTimelineVideoElement,
  );

  return (
    <Fragment>
      <VideoPreset activeElement={displayElement} url={sourceUrl} />

      <ElementAnimationSettings activeElement={activeTimelineVideoElement} />

      <GroupContent>
        <Item>
          <PropertyCaption>Start</PropertyCaption>
          <PropertyNumericalInput
            activeElement={activeTimelineVideoElement}
            propertyName="time"
            defaultValue="auto"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              const newVideoOverlays = await fadeProducer.resetCrossfadeOnVideo(
                parseFloat(value),
                activeTimelineVideoElement.duration,
              );

              // Apply fade overlays using legacy method (complex video overlay logic)
              if (Object.keys(newVideoOverlays).length > 0) {
                await videoCreator.applyVideoStateModifications(
                  newVideoOverlays,
                  true,
                  'changing video placement',
                );
              }

              // Update the main property using headless editor
              await updateTimelineVideoProperty({ [property]: value });
            }}
          />
        </Item>

        <Item>
          <PropertyCaption>Duration</PropertyCaption>
          <PropertyNumericalInput
            activeElement={activeTimelineVideoElement}
            propertyName="duration"
            defaultValue="auto"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              const newVideoOverlays = await fadeProducer.resetCrossfadeOnVideo(
                activeTimelineVideoElement.source.time,
                parseFloat(value),
              );

              // Apply fade overlays using legacy method (complex video overlay logic)
              if (Object.keys(newVideoOverlays).length > 0) {
                await videoCreator.applyVideoStateModifications(
                  newVideoOverlays,
                  true,
                  'changing video placement',
                );
              }

              // Update the main property using headless editor
              await updateTimelineVideoProperty({ [property]: value });
            }}
          />
        </Item>

        {/* Layout/Fit dropdown - show all options */}
        <Item>
          <PropertyCaption>
            {isLayoutComposition ? 'Layout Type' : 'Fit'}
          </PropertyCaption>
          <VideoFitPropertySelect
            activeElement={activeTimelineVideoElement}
            propertyName="fit"
            defaultValue={isLayoutComposition ? 'stacked-1-1' : FIT_DEFAULT}
            options={[
              { caption: 'Cover', value: 'cover' },
              { caption: 'Contain', value: 'contain' },
              { caption: 'Fill', value: 'fill' },
              ...(enableStackedLayouts &&
              videoCreator.currentVideo?.aspectRatio === AspectRatio.AR_9_16
                ? [
                    { caption: 'Stacked 1:1', value: 'stacked-1-1' },
                    { caption: 'Stacked 1:3', value: 'stacked-1-3' },
                  ]
                : []),
            ]}
          />
        </Item>

        {/* Add a second fit dropdown for the child video element when we're inside a composition */}
        {childVideoElement && (
          <Item>
            <PropertyCaption>Inner Video Fit</PropertyCaption>
            <VideoFitPropertySelect
              activeElement={childVideoElement}
              propertyName="fit"
              defaultValue={FIT_DEFAULT}
              options={[
                { caption: 'Cover', value: 'cover' },
                { caption: 'Contain', value: 'contain' },
                { caption: 'Fill', value: 'fill' },
              ]}
            />
          </Item>
        )}

        {isBroll && (
          <Item>
            <PropertyCaption>Volume</PropertyCaption>
            <PropertyNumericalInput
              activeElement={activeTimelineVideoElement}
              propertyName="volume"
              defaultValue="100"
              unit="%"
              customStyles={numericalInputStyles}
              getInputValue={async (property: string, value: string) => {
                await updateTimelineVideoProperty({ [property]: value });
              }}
            />
          </Item>
        )}
      </GroupContent>

      <GenericAnimationSettings
        hideFadeAnimations
        activeElement={activeTimelineVideoElement}
      />

      <GroupContent>
        <Item>
          <PropertyCaption>Audio Fade In</PropertyCaption>
          <PropertyNumericalInput
            activeElement={activeTimelineVideoElement}
            propertyName="audio_fade_in"
            defaultValue="0"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              await updateTimelineVideoProperty({
                [property]: parseFloat(value),
              });
            }}
          />
        </Item>
        <Item>
          <PropertyCaption>Audio Fade Out</PropertyCaption>
          <PropertyNumericalInput
            activeElement={activeTimelineVideoElement}
            propertyName="audio_fade_out"
            defaultValue="0"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              await updateTimelineVideoProperty({
                [property]: parseFloat(value),
              });
            }}
          />
        </Item>
      </GroupContent>
      <GroupContent>
        <Item>
          <PropertyCaption>Horizontal Alignment</PropertyCaption>
          <SliderAndIcon>
            <UpDownIcon rotate={90} />
            <SliderSelect
              getValue={newValue => modifyPropertyWithSlider('x', newValue)}
              defaultValue={50}
              currValue={xValue}
              min={0}
              max={100}
            />
          </SliderAndIcon>
        </Item>
        <Item>
          <PropertyCaption>Vertical Alignment</PropertyCaption>
          <SliderAndIcon>
            <UpDownIcon />
            <SliderSelect
              getValue={newValue => modifyPropertyWithSlider('y', newValue)}
              defaultValue={50}
              currValue={yValue}
              min={0}
              max={100}
            />
          </SliderAndIcon>
        </Item>
        <Item>
          <PropertyCaption>Zoom In/Out</PropertyCaption>
          <SliderAndIcon>
            <ScaleIcon />
            <SliderSelect
              getValue={newValue => handleChangeScale(newValue)}
              defaultValue={100}
              currValue={(() => {
                const element = displayElement;
                if (
                  element &&
                  element.source &&
                  element.source.width !== undefined
                ) {
                  const width = element.source.width.toString();
                  const widthVal = parseFloat(width);
                  return !isNaN(widthVal) ? widthVal : 100;
                }
                return 100;
              })()}
              min={100}
              max={300}
            />
          </SliderAndIcon>
        </Item>
      </GroupContent>
    </Fragment>
  );
};

export const GroupContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 10px;
`;

export const Item = styled.div``;

const SliderAndIcon = styled.div`
  display: flex;
  border: 1px solid #484848;
  border-radius: 8px;
  padding: 12px;
  gap: 5px;
  align-items: center;
  height: 15px;
`;
